/**
 * The drink leaving the sheet and landing in the basket.
 *
 * `add()` closes the sheet in the same turn, so the photograph that was just
 * chosen would vanish before the customer saw it go anywhere. This clones the
 * painted cup — not the letterboxed hero box — and carries it to `[data-basket]`
 * on its own layer, above the sheet. The basket pops when the flight ends,
 * not when the tap lands.
 */

export const BASKET_CATCH = 'mgc:basket-catch'

export function containedRect(
  box: { left: number; top: number; width: number; height: number },
  natural: { width: number; height: number }
): { left: number; top: number; width: number; height: number } {
  if (natural.width === 0 || natural.height === 0) return box
  const scale = Math.min(box.width / natural.width, box.height / natural.height)
  const width = natural.width * scale
  const height = natural.height * scale
  return {
    left: box.left + (box.width - width) / 2,
    top: box.top + (box.height - height) / 2,
    width,
    height
  }
}

export type Flight = {
  src: string
  start: { left: number; top: number; width: number; height: number }
  end: { left: number; top: number; width: number; height: number }
}

let announce: ((flight: Flight) => void) | null = null
let dismiss: (() => void) | null = null

export function bindFlyer(next: ((flight: Flight) => void) | null, stop: (() => void) | null): void {
  announce = next
  dismiss = stop
}

export function cancelFlight(): void {
  dismiss?.()
}

export function catchBasket(): void {
  window.dispatchEvent(new Event(BASKET_CATCH))
}

/** Measure and launch. False means the caller should pop the basket itself. */
export function flyIntoBasket(from: HTMLElement | null, src: string | undefined): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false
  if (from === null || src === undefined || src.length === 0) return false
  const img = from.querySelector('img')
  if (img === null || img.naturalWidth === 0) return false
  const basket = document.querySelector('[data-basket]')
  if (!(basket instanceof HTMLElement)) return false
  const start = containedRect(img.getBoundingClientRect(), {
    width: img.naturalWidth,
    height: img.naturalHeight
  })
  const end = basket.getBoundingClientRect()
  if (start.width < 8 || end.width < 8) return false
  announce?.({ src, start, end })
  return announce !== null
}
